import { useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { dateForOffset, useApp } from '../state/app.js';
import { Spinner } from '../ui/common.js';
import { OvertimeHistory } from './OvertimeHistory.js';
import { STATUS, type WorkStatus } from '../ui/status.js';
import { kstHHmm, mmdd, mmddW, boardStatus, kstToday } from '../lib/format.js';

const ORDER: WorkStatus[] = ['working', 'trip', 'away', 'vacation', 'off', 'absent'];

/** monthOffset(0=이번 달, -1=지난 달) → 해당 월의 시작/끝 일자 + 라벨 (KST 기준) */
function monthRange(offset: number): { start: string; end: string; label: string } {
  const [y, m] = kstToday().split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1 + offset, 1));
  const yy = base.getUTCFullYear();
  const mm = base.getUTCMonth(); // 0-based
  const p2 = (n: number) => String(n).padStart(2, '0');
  const lastDay = new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
  return {
    start: `${yy}-${p2(mm + 1)}-01`,
    end: `${yy}-${p2(mm + 1)}-${p2(lastDay)}`,
    label: `${yy}.${p2(mm + 1)}`,
  };
}

// 휴가 상태별 배지 색상
const LEAVE_BADGE: Record<string, { bg: string; fg: string }> = {
  승인: { bg: '#DCFCE7', fg: '#15803D' },
  승인대기: { bg: '#FEF3C7', fg: '#B45309' },
  상신: { bg: '#FEF3C7', fg: '#B45309' },
  반려: { bg: '#FEE2E2', fg: '#B91C1C' },
  취소: { bg: '#F3F4F6', fg: '#9CA3AF' },
};

export function Stats(): JSX.Element {
  const { dateOffset } = useApp();
  const date = dateForOffset(dateOffset).date;
  const [person, setPerson] = useState('');
  const [monthOffset, setMonthOffset] = useState(0); // 출퇴근 이력 월 페이징

  const summary = useQuery({ queryKey: ['stats', date], queryFn: () => api.statsSummary(date) });
  const board = useQuery({ queryKey: ['board', date], queryFn: () => api.board(date) });
  const mr = monthRange(monthOffset);
  const hist = useQuery({ queryKey: ['attendance', 'admin', mr.start, mr.end], queryFn: () => api.attendanceAdmin(mr.start, mr.end) });

  // 대표이사(출퇴근 관리 예외, status='exempt')는 통계 집계에서 제외
  const statEntries = (board.data?.entries ?? []).filter((e) => e.status !== 'exempt');
  const people = statEntries.map((e) => ({ upn: e.upn, name: e.name }));
  const selUpn = person || people[0]?.upn || '';

  // 상태 분포 / 부서별 출근율
  const { counts, total, deptBars } = useMemo(() => {
    const c: Record<WorkStatus, number> = { working: 0, away: 0, vacation: 0, off: 0, absent: 0, trip: 0, exempt: 0 };
    const byDept = new Map<string, { present: number; total: number }>();
    for (const e of statEntries) {
      const s = boardStatus(e);
      c[s] += 1;
      const d = byDept.get(e.dept) ?? { present: 0, total: 0 };
      d.total += 1;
      if (s === 'working' || s === 'away') d.present += 1;
      byDept.set(e.dept, d);
    }
    const bars = Array.from(byDept.entries()).map(([dept, v]) => ({ dept, pct: v.total ? Math.round((v.present / v.total) * 100) : 0 }));
    return { counts: c, total: statEntries.length, deptBars: bars };
  }, [statEntries]);

  const donut = useMemo(() => {
    if (total === 0) return 'conic-gradient(#E5E7EB 0 100%)';
    let acc = 0;
    const stops = ORDER.map((s) => {
      const from = (acc / total) * 100;
      acc += counts[s];
      const to = (acc / total) * 100;
      return `${STATUS[s].dot} ${from}% ${to}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  }, [counts, total]);

  const personRows = useMemo(() => {
    const items = (hist.data?.items ?? []).filter((a) => a.empUpn === selUpn);
    return items
      .slice()
      .sort((a, b) => (a.workDate < b.workDate ? 1 : -1))
      .map((a) => ({
        date: mmddW(a.workDate),
        in: kstHHmm(a.checkInAt),
        out: a.checkInAt && !a.checkOutAt ? (a.workDate === kstToday() ? '근무 중' : '—') : kstHHmm(a.checkOutAt),
        tag: a.checkInAt ? '정상' : '미기록',
      }));
  }, [hist.data, selUpn]);

  // 개인별 휴가 이력 (선택 직원)
  const leaveHist = useQuery({
    queryKey: ['leave', 'admin', selUpn],
    queryFn: () => api.leaveByPerson(selUpn),
    enabled: !!selUpn,
  });
  const leaveRows = useMemo(() => {
    return (leaveHist.data?.items ?? [])
      .slice()
      .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
      .map((l) => ({
        id: l.id,
        period: l.startDate === l.endDate ? mmdd(l.startDate) : `${mmdd(l.startDate)} ~ ${mmdd(l.endDate)}`,
        type: l.type,
        days: l.days,
        status: l.status,
      }));
  }, [leaveHist.data]);

  const pct = (r?: number) => `${Math.round((r ?? 0) * 100)}%`;

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px,1fr))', gap: 14, marginBottom: 16 }}>
        <Kpi label="금일 출근율" value={pct(summary.data?.attendanceRate)} color="#2563EB" sub="출근 / 전체 인원" />
        <Kpi label="휴가 사용률" value={pct(summary.data?.leaveUsageRate)} sub="연차 소진율" />
        <Kpi label="승인 대기" value={`${summary.data?.pendingApprovals ?? 0}건`} color="#B45309" sub="결재 대기 휴가" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 16, marginBottom: 16 }}>
        {/* 부서별 출근율 */}
        <div className="dx-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 18 }}>부서별 출근율</div>
          {deptBars.length === 0 ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>데이터가 없습니다.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {deptBars.map((b) => (
                <div key={b.dept} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 64, flexShrink: 0, fontSize: 13, color: '#4B5563' }}>{b.dept}</div>
                  <div style={{ flex: 1, height: 11, background: '#EDEFF2', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${b.pct}%`, height: '100%', background: '#2563EB', borderRadius: 6 }} />
                  </div>
                  <div className="tnum" style={{ width: 38, flexShrink: 0, textAlign: 'right', fontSize: 13, fontWeight: 600 }}>{b.pct}%</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 금일 상태 분포 도넛 */}
        <div className="dx-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>금일 상태 분포</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 148, height: 148, borderRadius: '50%', background: donut }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div className="tnum" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{total}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>전체 인원</div>
              </div>
              <div style={{ position: 'absolute', inset: 26, background: '#fff', borderRadius: '50%' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1, minWidth: 120 }}>
              {ORDER.map((s) => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS[s].dot, flexShrink: 0 }} />
                  <span style={{ color: '#4B5563' }}>{STATUS[s].label}</span>
                  <span className="tnum" style={{ marginLeft: 'auto', fontWeight: 600 }}>{counts[s]}명</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 개인 출퇴근 이력 (월별 페이징) */}
      <div className="dx-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>개인 출퇴근 이력</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <button className="dx-btn dx-btn-ghost" type="button" aria-label="이전 달" style={{ padding: '5px 10px', fontSize: 14, lineHeight: 1 }} onClick={() => setMonthOffset((o) => o - 1)}>‹</button>
            <div className="tnum" style={{ minWidth: 64, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#374151' }}>{mr.label}</div>
            <button className="dx-btn dx-btn-ghost" type="button" aria-label="다음 달" disabled={monthOffset >= 0} style={{ padding: '5px 10px', fontSize: 14, lineHeight: 1, opacity: monthOffset >= 0 ? 0.4 : 1 }} onClick={() => setMonthOffset((o) => Math.min(0, o + 1))}>›</button>
          </div>
          <select className="dx-select" value={selUpn} onChange={(e) => setPerson(e.target.value)}>
            {people.map((p) => <option key={p.upn} value={p.upn}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 420 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', background: '#F9FAFB', borderBottom: '1px solid #EDEFF2', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
              <div style={{ padding: '9px 16px' }}>날짜</div><div style={{ padding: '9px 12px' }}>출근</div><div style={{ padding: '9px 12px' }}>퇴근</div><div style={{ padding: '9px 12px' }}>구분</div>
            </div>
            {hist.isLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
            ) : personRows.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>{mr.label} 출퇴근 이력이 없습니다.</div>
            ) : personRows.map((h, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', borderBottom: '1px solid #F3F4F6', fontSize: 13, alignItems: 'center' }}>
                <div style={{ padding: '10px 16px', color: '#4B5563' }}>{h.date}</div>
                <div className="tnum" style={{ padding: '10px 12px' }}>{h.in}</div>
                <div className="tnum" style={{ padding: '10px 12px', color: '#6B7280' }}>{h.out}</div>
                <div style={{ padding: '10px 12px' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999, ...(h.tag === '정상' ? { background: '#DCFCE7', color: '#15803D' } : { background: '#F3F4F6', color: '#9CA3AF' }) }}>{h.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 개인별 휴가 이력 (선택 직원 — 출퇴근 이력과 동일 직원 기준) */}
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>개인 휴가 이력</div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#9CA3AF' }}>{people.find((p) => p.upn === selUpn)?.name ?? ''}</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 420 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr .8fr 1fr', background: '#F9FAFB', borderBottom: '1px solid #EDEFF2', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
              <div style={{ padding: '9px 16px' }}>기간</div><div style={{ padding: '9px 12px' }}>종류</div><div style={{ padding: '9px 12px' }}>일수</div><div style={{ padding: '9px 12px' }}>상태</div>
            </div>
            {leaveHist.isLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
            ) : leaveRows.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>휴가 이력이 없습니다.</div>
            ) : leaveRows.map((l) => {
              const badge = LEAVE_BADGE[l.status] ?? LEAVE_BADGE['취소'];
              return (
                <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr .8fr 1fr', borderBottom: '1px solid #F3F4F6', fontSize: 13, alignItems: 'center' }}>
                  <div className="tnum" style={{ padding: '10px 16px', color: '#4B5563' }}>{l.period}</div>
                  <div style={{ padding: '10px 12px', color: '#4B5563' }}>{l.type}</div>
                  <div className="tnum" style={{ padding: '10px 12px', color: '#6B7280' }}>{l.days}일</div>
                  <div style={{ padding: '10px 12px' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: badge.bg, color: badge.fg }}>{l.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* 개인 야근 이력(월별) */}
      <OvertimeHistory people={people} defaultUpn={selUpn} />
    </div>
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }): JSX.Element {
  return (
    <div className="dx-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>{label}</div>
      <div className="tnum" style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-.02em', marginTop: 6, color: color ?? '#1F2937' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3 }}>{sub}</div>
    </div>
  );
}
